<template>
    <div class="no-container relative bg-green-600 py-10 text-white" :class="containerClasses">
        <!-- Background gradient overlay for headerBgImage -->
        <div
            v-if="isHeaderBgImage"
            class="absolute top-0 h-full w-full bg-gradient-to-b from-transparent to-black/50"
            aria-hidden="true"></div>
        <!-- Header with background image variant -->
        <div v-if="isHeaderBgImage" class="relative">
            <!-- Tablet (md..lg) gets the mobile art direction: the desktop crop assumes the wide layout -->
            <NuxtImg
                v-if="image"
                class="hidden h-auto w-full object-cover lg:block"
                :alt="image?.imageAlt || pageTitle || 'Hero image'"
                :src="imageUrl"
                width="1920"
                height="800"
                loading="eager"
                fetchpriority="high" />
            <NuxtImg
                v-if="mobileImage"
                class="h-auto w-full object-cover lg:hidden"
                :alt="mobileImage?.imageAlt || pageTitle || 'Hero image'"
                :src="mobileImageUrl"
                width="768"
                height="800"
                loading="eager"
                fetchpriority="high" />
            <NuxtImg
                v-else-if="image"
                class="h-auto w-full object-cover lg:hidden"
                :alt="image?.imageAlt || pageTitle || 'Hero image'"
                :src="imageUrl"
                width="768"
                height="800"
                loading="eager"
                fetchpriority="high" />
            <div class="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" aria-hidden="true"></div>
            <div class="absolute inset-0 z-10 flex flex-col justify-end">
                <div
                    class="normal-container relative z-10 !mx-6 pb-16 min-[1920px]:!mx-auto sm:!mx-16 lg:grid lg:grid-cols-1 lg:pt-80">
                    <div
                        class="sanity-content page-content-hero-sanity-content mb-0 flex h-full flex-col items-center justify-center text-center sm:px-2 lg:px-10 2xl:px-40">
                        <div class="btn-center flex flex-col justify-center pt-10 lg:w-8/12 lg:py-0">
                            <SanityContent :blocks="contentBlocks" :serializers="serializers" />
                        </div>
                        <a
                            href="#block-1"
                            class="mt-2 flex items-center justify-center border-b-2 border-white p-0 pb-0.5 !no-underline hover:!border-green-600 hover:text-green-600"
                            aria-label="Continue reading"
                            @click.prevent="handleContinueReadingClick('#block-1')">
                            Lees verder
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <!-- Standard header variant -->
        <div v-else>
            <div v-if="showBreadcrumbs" class="normal-container mb-10">
                <ShopwareBreadcrumbs :breadcrumbPrefixEnabled="false" />
            </div>
            <div class="normal-container relative z-10 lg:grid lg:grid-cols-2 lg:gap-10">
                <!-- Content section -->
                <div
                    class="sanity-content page-content-hero-sanity-content mb-10 flex h-full min-w-0 flex-col justify-center lg:mb-0">
                    <div class="flex flex-col justify-center">
                        <SanityContent :blocks="content" :serializers="serializers" />
                    </div>
                </div>

                <!-- Image section (min-w-0: grid child would otherwise refuse to shrink below the img's intrinsic 600px and overflow on tablet) -->
                <div class="relative min-w-0 pl-2 lg:pl-0">
                    <!-- Desktop image card -->
                    <!-- Desktop card only from lg: its 600px-intrinsic object-cover crop clipped the
                         image on tablets (768-1023), where the layout is still the stacked column -->
                    <component
                        :is="hasImageButtonUrl ? 'NuxtLink' : 'div'"
                        v-if="image"
                        :to="hasImageButtonUrl ? `/${imageButtonUrl}` : undefined"
                        class="relative hidden overflow-hidden lg:block"
                        :aria-label="hasImageButtonUrl ? image?.imageAlt || highlightTitle || 'View more' : undefined">
                        <!-- Badge -->
                        <div
                            v-if="badge"
                            class="absolute right-16 z-20 hidden w-44 bg-white px-4 py-[52px] text-center text-black lg:block lg:w-44 xl:w-60">
                            <p class="font-editorial-new mb-4 text-xs break-words uppercase lg:mb-3 xl:mb-6">
                                {{ badge.title }}
                            </p>
                            <h3 class="mb-0 text-xl !leading-[33px] break-words hyphens-auto lg:text-lg xl:text-2xl">
                                {{ badge.subtitle }}
                            </h3>
                        </div>
                        <!-- Shadow box -->
                        <div
                            class="box-shadow-size absolute right-0 bottom-0 rounded-br-[40px] bg-green-500 lg:rounded-br-3xl"></div>
                        <!-- Image -->
                        <NuxtImg
                            class="relative z-10 h-[443px] w-full object-cover pr-6 pb-6 lg:h-[596px] lg:max-h-[600px]"
                            :alt="image.imageAlt || highlightTitle || 'Hero image'"
                            :src="getImageUrl(image)"
                            width="600"
                            height="600" />
                        <!-- Overlay content -->
                        <div
                            class="absolute bottom-0 z-10 mb-5 flex w-full flex-col items-center pr-6 pb-[45px] text-center">
                            <h2 v-if="highlightTitle" class="mb-4 !text-base lg:!text-2xl">{{ highlightTitle }}</h2>
                            <Button
                                v-if="image.button?.title && hasImageButtonUrl"
                                :type="image.button.type"
                                :link="imageButtonUrl"
                                buttonType="center">
                                {{ image.button.title }}
                            </Button>
                        </div>
                    </component>

                    <!-- Mobile image card -->
                    <!-- Falls back to the desktop image so tablets never lose the card entirely -->
                    <component
                        :is="hasMobileCardButtonUrl ? 'NuxtLink' : 'div'"
                        v-if="mobileCardImage"
                        :to="hasMobileCardButtonUrl ? `/${mobileCardButtonUrl}` : undefined"
                        class="relative overflow-hidden md:mx-auto md:max-w-[520px] lg:hidden"
                        :aria-label="
                            hasMobileCardButtonUrl
                                ? mobileCardImage?.imageAlt || highlightTitle || 'View more'
                                : undefined
                        ">
                        <!-- Shadow box -->
                        <div
                            class="box-shadow-size absolute right-0 bottom-0 rounded-br-[40px] bg-green-500 lg:rounded-br-3xl"></div>
                        <!-- Image -->
                        <!-- Tablet keeps the natural ratio so banner artwork is never cropped -->
                        <NuxtImg
                            class="relative z-10 h-[443px] w-full object-cover pr-6 pb-6 md:h-auto lg:h-[596px] lg:max-h-[600px]"
                            :alt="mobileCardImage.imageAlt || highlightTitle || 'Hero image'"
                            :src="getImageUrl(mobileCardImage)"
                            width="600"
                            height="443" />
                        <!-- Overlay content -->
                        <div
                            class="absolute bottom-0 z-10 mb-5 flex w-full flex-col items-center pr-6 pb-[45px] text-center">
                            <h2 v-if="highlightTitle" class="mb-4 !text-base lg:!text-2xl">{{ highlightTitle }}</h2>
                            <Button
                                v-if="mobileCardImage.button?.title && hasMobileCardButtonUrl"
                                :type="mobileCardImage.button.type"
                                :link="mobileCardButtonUrl"
                                buttonType="center">
                                {{ mobileCardImage.button.title }}
                            </Button>
                        </div>
                    </component>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, computed, watch, onMounted } from 'vue';

    interface Button {
        title?: string;
        type?: string;
        buttonType?: 'external' | 'internal';
        externalUrl?: string;
        internalLink?: {
            _ref: string;
        };
    }

    interface Image {
        imageAlt?: string;
        button?: Button;
    }

    interface Badge {
        title?: string;
        subtitle?: string;
    }

    interface Props {
        headerType?: string;
        content?: any;
        image?: Image;
        mobileImage?: Image;
        highlightTitle?: string;
        badge?: Badge;
        link?: string;
        pageTitle?: string;
        showBreadcrumbs?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        headerType: '',
        highlightTitle: '',
        pageTitle: '',
        showBreadcrumbs: true,
    });

    const imageButtonUrl = ref<string>('');
    const mobileImageButtonUrl = ref<string>('');

    // Computed properties
    const isHeaderBgImage = computed(() => props.headerType === 'headerBgImage');

    const containerClasses = computed(() => ({
        // Pull the hero under the header only from xl: below that the header is the ~3-bar
        // mobile/tablet stack (~140px), not the 62px desktop bar this offset was tuned for
        '!bg-transparent !py-0 xl:-mt-[62px]': isHeaderBgImage.value,
    }));

    const imageUrl = computed(() => {
        if (!props.image) return '';
        return useImageUrl(props.image).url();
    });

    const mobileImageUrl = computed(() => {
        if (!props.mobileImage) return '';
        return useImageUrl(props.mobileImage).url();
    });

    const hasImageButtonUrl = computed(() => Boolean(imageButtonUrl.value && imageButtonUrl.value !== '#'));
    const hasMobileImageButtonUrl = computed(() =>
        Boolean(mobileImageButtonUrl.value && mobileImageButtonUrl.value !== '#'),
    );

    // The mobile/tablet card falls back to the desktop image when no mobile image is set
    const mobileCardImage = computed(() => props.mobileImage ?? props.image);
    const mobileCardButtonUrl = computed(() => (props.mobileImage ? mobileImageButtonUrl.value : imageButtonUrl.value));
    const hasMobileCardButtonUrl = computed(() =>
        props.mobileImage ? hasMobileImageButtonUrl.value : hasImageButtonUrl.value,
    );

    const contentBlocks = computed(() => props.content as any);

    const CONTINUE_SCROLL_OFFSET = 120;

    const serializers = {
        types: {
            button: resolveComponent('Button'),
        },
    };

    // Methods
    function getImageUrl(img: Image): string {
        if (!img) return '';
        return useImageUrl(img).url();
    }

    async function getUrl(button: Button): Promise<string> {
        if (!button) return '';

        try {
            if (button.buttonType === 'external' && button.externalUrl) {
                return button.externalUrl;
            }

            if (button.buttonType === 'internal' && button.internalLink?._ref) {
                const buttonData = await useGetSeoUrl(button.internalLink._ref);
                const seoPath = buttonData[0]?.seoUrls[0]?.seoPathInfo;

                if (seoPath) {
                    return seoPath.toLowerCase();
                }
            }

            return '';
        } catch {
            return '';
        }
    }

    async function fetchUrls(): Promise<void> {
        if (props.image?.button) {
            imageButtonUrl.value = await getUrl(props.image.button);
        } else {
            imageButtonUrl.value = '';
        }

        if (props.mobileImage?.button) {
            mobileImageButtonUrl.value = await getUrl(props.mobileImage.button);
        } else {
            mobileImageButtonUrl.value = '';
        }
    }

    function handleContinueReadingClick(hash: string): void {
        if (process.server) return;
        const selector = hash.startsWith('#') ? hash : `#${hash}`;
        const target = document.querySelector(selector);
        if (!target) return;

        const targetTop = target.getBoundingClientRect().top + window.pageYOffset - CONTINUE_SCROLL_OFFSET;

        window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: 'smooth',
        });
    }

    // Lifecycle
    onMounted(fetchUrls);

    watch(() => [props.image, props.mobileImage], fetchUrls, { deep: true, immediate: true });
</script>

<style scoped>
    .box-shadow-size {
        width: calc(100% - 24px);
        height: calc(100% - 24px);
    }
</style>
